import { AudioRecorderComponent } from '@zaoblako/web/shared/dumbs/audio-recorder/audio-recorder.component';
import { AiVoiceAssistantService } from '@zaoblako/web/core/services/ai/ai-voice-assistant.service';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { VoiceAssistantBridgeService } from '@zaoblako/web/shared/voice-assistant/services/voice-assistant-bridge.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { formTypes, formTitles, formWidths } from '@zaoblako/web/shared/voice-assistant/constants/form-type.constants';

import {
  Component,
  ChangeDetectionStrategy,
  Input,
  ChangeDetectorRef,
  type OnDestroy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import { ButtonModule } from 'primeng/button';
import type { Subscription } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-voice-assistant-modal',
  styleUrl: 'voice-assistant-modal.component.scss',
  templateUrl: './voice-assistant-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TooltipModule,
    DialogModule,
    ButtonModule
  ]
})
export class VoiceAssistantModalComponent extends AudioRecorderComponent implements OnDestroy {
  // Input props

  /** Personal account mode flag */
  @Input() public isLk = false;

  // State props

  /** Modal opened flag */
  public isOpened: boolean;

  /** Voice assistant command canceled */
  public isCanceled: boolean;

  private openFormSubscription$: Subscription;

  public constructor(
    private readonly aiVoiceAssistantService: AiVoiceAssistantService,
    private readonly formStoreService: FormStoreService,
    private readonly voiceAssistantBridgeService: VoiceAssistantBridgeService,
    errorService: ErrorService,
    cdRef: ChangeDetectorRef
  ) {
    super(errorService, cdRef);
  }

  public ngOnDestroy(): void {
    this.openFormSubscription$?.unsubscribe();
  }

  public onDialogStart(): void {
    // Subscribe to MP3 recording complete
    this.openFormSubscription$ = this.onMp3Data$
      .asObservable()
      .subscribe((mp3Data) => this.openForm(mp3Data));

    this.openModal();
    this.startRecording();
  }

  public onCancel(): void {
    this.openFormSubscription$?.unsubscribe();
    this.stopRecording();
  }

  public onSubmit(): void {
    this.stopRecording();
    this.closeModal();
  }

  private openModal(): void {
    this.isOpened = true;
    this.cdRef.markForCheck();
  }

  private closeModal(): void {
    this.isOpened = false;
    this.cdRef.markForCheck();
  }

  private openForm(mp3Data: BlobPart[]): void {
    const file = new File(mp3Data, 'open-form-command.mp3', {
      type: 'audio/mp3',
      lastModified: Date.now()
    });
    this.aiVoiceAssistantService
      .openForm(file)
      .subscribe({
        next: (form) => {
          this.closeModal();

          if (!form) {
            this.errorService.addCustomError('Ошибка распознавания', 'Не удалось определить действие');
            return;
          }

          const { onSave } = this.formStoreService.addForm(formTypes[form], {
            headerText: formTitles[form],
            widthSize: formWidths[form],
            isNotShowRedirectInCreate: true
          });

          let onSaveSubscription: Subscription;
          const onSaveSubscriptionDrop = () => onSaveSubscription?.unsubscribe();

          onSaveSubscription = onSave.subscribe({
            next: (payload) => {
              this.voiceAssistantBridgeService.formSaved(form, payload);
              onSaveSubscriptionDrop();
            },
            error: onSaveSubscriptionDrop
          });
        },
        error: () => this.closeModal()
      });
  }
}
